/**
 * LayoutCalcs
 * ===========
 *
 * A singleton layout service that:
 * - Measures layout-related DOM elements (e.g. header, footer)
 * - Exposes those measurements as:
 *   - CSS custom properties
 *   - JavaScript-accessible values
 * - Automatically stays in sync when layout changes
 *
 * ------------------------------------------------------------
 * USAGE
 * ------------------------------------------------------------
 *
 * 1) Initialise ONCE (typically in your main entry file):
 *
 *   import layout from './LayoutCalcs.js';
 *
 *   layout.init({
 *     header: {
 *       selector: '.site-header',
 *       var: '--header-height'
 *     },
 *     footer: {
 *       selector: '.site-footer',
 *       var: '--footer-height'
 *     }
 *   });
 *
 *
 * 2) Consume values anywhere else:
 *
 *   import layout from './LayoutCalcs.js';
 *
 *   // One-off read
 *   const headerHeight = layout.get('header');
 *
 *   // React to changes
 *   layout.subscribe(({ header, footer }) => {
 *     console.log(header, footer);
 *   });
 *
 *
 * 3) Use in CSS:
 *
 *   main {
 *     min-height: calc(100vh - var(--header-height) - var(--footer-height));
 *   }
 *
 * ------------------------------------------------------------
 * DESIGN NOTES
 * ------------------------------------------------------------
 *
 * - This module is a singleton by virtue of ES module caching.
 * - Layout is measured once, then shared everywhere.
 * - CSS and JS always agree on values.
 * - No guessing lifecycle events (load / DOMContentLoaded).
 * - Reacts to:
 *   - resize
 *   - mobile viewport changes
 *   - font loading
 *   - element resizes (via ResizeObserver)
 */

class LayoutCalcs {
    constructor() {
        /**
         * Configuration passed in via init().
         * Shape:
         * {
         *   key: { selector, var, mode? }
         * }
         */
        this.config = {};

        /**
         * Cached DOM elements resolved from selectors.
         * Keyed by config key.
         */
        this.elements = {};

        /**
         * Latest calculated layout values.
         * These are the canonical values used by JS.
         */
        this.values = {};

        /**
         * Subscribers that should be notified when values change.
         */
        this.subscribers = new Set();

        /**
         * ResizeObservers attached to elements.
         */
        this.observers = [];

        /**
         * requestAnimationFrame id for resize throttling.
         */
        this.raf = null;

        /**
         * Guard to ensure init() only runs once.
         */
        this.initialised = false;
    }

    /**
     * Initialise the layout service.
     * This should be called ONCE from an entry point.
     *
     * @param {Object} config - Layout configuration map
     */
    init(config) {
        if (this.initialised) return;

        this.initialised = true;
        this.config = config;

        this.bindElements();
        this.bindObservers();
        this.bindGlobalEvents();

        // Initial calculation
        this.calculate();

        // Fonts can change layout once they load
        document.fonts?.ready.then(() => this.calculate());
    }

    /**
     * Resolve selectors from the config into DOM elements.
     * Elements are cached so we don’t repeatedly query the DOM.
     */
    bindElements() {
        for (const key in this.config) {
            const { selector } = this.config[key];
            const el = document.querySelector(selector);
            if (el) {
                this.elements[key] = el;
            }
        }
    }

    /**
     * Attach ResizeObservers to each tracked element.
     * This allows us to react to:
     * - font swaps
     * - image loading
     * - content changes
     */
    bindObservers() {
        if (!('ResizeObserver' in window)) return;

        for (const key in this.elements) {
            const observer = new ResizeObserver(() => this.onResize());
            observer.observe(this.elements[key]);
            this.observers.push(observer);
        }
    }

    /**
     * Bind global events that affect layout.
     */
    bindGlobalEvents() {
        window.addEventListener('resize', this.onResize, { passive: true });

        // visualViewport handles mobile browser chrome changes
        window.visualViewport?.addEventListener('resize', this.onResize, {
            passive: true
        });
    }

    /**
     * Resize handler.
     * Throttled using requestAnimationFrame to avoid layout thrashing.
     */
    onResize = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.calculate());
    };

    /**
     * Perform all layout calculations.
     * This is the single source of truth for values.
     *
     * Flow:
     * 1) Read layout values
     * 2) Store them internally
     * 3) Write CSS custom properties
     * 4) Notify subscribers
     */
    calculate() {
        const root = document.documentElement;

        /* ---------------- Viewport ---------------- */

        const viewportHeight =
            window.visualViewport?.height || window.innerHeight;

        const vh = +((viewportHeight * 0.01).toFixed(2));

        this.values.vh = vh;
        root.style.setProperty('--vh', `${vh}px`);

        /* ---------------- Elements ---------------- */

        for (const key in this.elements) {
            const el = this.elements[key];
            const { var: cssVar, mode = 'offsetHeight' } = this.config[key];

            const value =
                mode === 'rect'
                    ? el.getBoundingClientRect().height
                    : el.offsetHeight;

            this.values[key] = Math.round(value);
            root.style.setProperty(cssVar, `${this.values[key]}px`);
        }

        this.notify();
    }

    /**
     * Get the current value for a specific layout key.
     * Useful for one-off, imperative reads.
     *
     * @param {string} key
     * @returns {number | undefined}
     */
    get(key) {
        return this.values[key];
    }

    /**
     * Subscribe to layout changes.
     * The callback is invoked every time layout values update.
     *
     * Returns an unsubscribe function to allow cleanup.
     *
     * @param {Function} fn
     * @returns {Function} unsubscribe
     */
    subscribe(fn) {
        this.subscribers.add(fn);
        return () => this.subscribers.delete(fn);
    }

    /**
     * Notify all subscribers with the latest values.
     * Values are shallow-copied to prevent mutation.
     */
    notify() {
        const snapshot = { ...this.values };
        this.subscribers.forEach(fn => fn(snapshot));
    }
}

/**
 * Export a single shared instance.
 * ES module caching guarantees this is a singleton.
 */
export default new LayoutCalcs();
